import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import React, { useEffect, useRef, Suspense } from "react";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { AuthProvider, useAuth, useUpgrade } from "@/context/AuthContext";
import { toast } from "sonner";
import { ThemeProvider } from "@/context/ThemeContext";
import { PrivacyProvider } from "@/context/PrivacyContext";
import UpgradeModal from "@/components/UpgradeModal";
import ChanakyaWidget from "@/components/ChanakyaWidget";
import NomineeLogin from "@/pages/NomineeLogin";
import SecurityOverlay from "@/components/SecurityOverlay";
import HandLoanTracker from "@/pages/HandLoanTracker";
// TEMP: ChitFund disabled — re-enable once tested
// import ChitFundTracker from "@/pages/ChitFundTracker";
// const ChitFundPublicPage = React.lazy(() => import('@/pages/ChitFundPublicPage'));
import CreditCardTracker from "@/pages/CreditCardTracker";
import TripPlanner from "@/pages/TripPlanner";
import GroupExpenses from "@/pages/GroupExpenses";
import LandingPage from "@/pages/LandingPage";
import LoginPage from "@/pages/LoginPage";
import SignupPage from "@/pages/SignupPage";
import Dashboard from "@/pages/Dashboard";
import BudgetManager from "@/pages/BudgetManager";
import EMIManager from "@/pages/EMIManager";
import FamilyManagement from "@/pages/FamilyManagement";
import Chatbot from "@/pages/Chatbot";
import SavingsGoals from "@/pages/SavingsGoals";
import InvestmentTracker from "@/pages/InvestmentTracker";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import OnboardingPage from "@/pages/OnboardingPage";
import SharedDashboard from "@/pages/SharedDashboard";
import SharedTrip from "@/pages/SharedTrip";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import TermsOfService from "@/pages/TermsOfService";
import ProfilePage from "@/pages/ProfilePage";
import FireCalculator from "@/pages/FireCalculator";
import InvestmentAdvisor from "@/pages/InvestmentAdvisor";
import FinancialCalendar from "@/pages/FinancialCalendar";
import IncomePage from "@/pages/IncomePage";
import DataManagement from "@/pages/DataManagement";
import HistoricalEntry from "@/pages/HistoricalEntry";
const AdminLogin     = React.lazy(() => import('@/pages/admin/AdminLogin'));
const AdminDashboard = React.lazy(() => import('@/pages/admin/AdminDashboard'));
import LifeProfile from "@/pages/LifeProfile";
import Playbook from "@/pages/Playbook";
import InsightsPage from "@/pages/InsightsPage";
import WhenToBuy from "@/pages/WhenToBuy";
import SmartBuy       from "@/pages/SmartBuy";
import QuickNotesPage from "@/pages/QuickNotesPage";

import CirclePage from "@/pages/CirclePage";
import GiftTracker from "@/pages/GiftTracker";
import RecurringExpenses from "@/pages/RecurringExpenses";
import LifetimeEarnings from "@/pages/LifetimeEarnings";
import SupportPage from "@/pages/SupportPage";
import { Toaster } from "@/components/ui/sonner";
import { DashboardProvider } from "@/context/DashboardContext";
import AppLayout    from '@/components/AppLayout';
import PublicShell  from '@/components/PublicShell';
import PageLoader   from '@/components/PageLoader';
import SplashLoader from '@/components/SplashLoader';
import CalcEmi      from '@/pages/CalcEmi';
import CalcSip      from '@/pages/CalcSip';
import CalcSalary   from '@/pages/CalcSalary';
import CalcTax      from '@/pages/CalcTax';
import MorePage     from '@/pages/MorePage';
import { ConfirmProvider } from '@/hooks/useConfirm';

const LearnHub           = React.lazy(() => import('@/pages/learn/LearnHub'));
const ArticlePage        = React.lazy(() => import('@/pages/learn/ArticlePage'));
const HowAIWorksPage     = React.lazy(() => import('@/pages/learn/HowAIWorksPage'));
// TEMP: Journeys disabled — re-enable once tested
// const JourneysHub        = React.lazy(() => import('@/pages/journeys/JourneysHub'));
// const StoryJourneyPage   = React.lazy(() => import('@/pages/journeys/StoryJourneyPage'));
const ToolsHub           = React.lazy(() => import('@/pages/tools/ToolsHub'));
const EMICalculatorPage  = React.lazy(() => import('@/pages/tools/EMICalculatorPage'));
const SIPCalculatorPage  = React.lazy(() => import('@/pages/tools/SIPCalculatorPage'));
const SalaryCalculatorPage = React.lazy(() => import('@/pages/tools/SalaryCalculatorPage'));
const FDCalculatorPage   = React.lazy(() => import('@/pages/tools/FDCalculatorPage'));
const PPFCalculatorPage  = React.lazy(() => import('@/pages/tools/PPFCalculatorPage'));
const FIRECalculatorPage = React.lazy(() => import('@/pages/tools/FIRECalculatorPage'));
const RentVsBuyPage      = React.lazy(() => import('@/pages/tools/RentVsBuyPage'));
const EducationLoanPage  = React.lazy(() => import('@/pages/tools/EducationLoanCalculatorPage'));
const LumpsumPage        = React.lazy(() => import('@/pages/tools/LumpsumCalculatorPage'));
const EPFPage            = React.lazy(() => import('@/pages/tools/EPFCalculatorPage'));
const XIRRPage           = React.lazy(() => import('@/pages/tools/XIRRCalculatorPage'));
const IncomeTaxPage      = React.lazy(() => import('@/pages/tools/IncomeTaxCalculatorPage'));
const HomeAffordabilityPage = React.lazy(() => import('@/pages/tools/HomeAffordabilityCalculatorPage'));
const RentVsBuyCalcPage     = React.lazy(() => import('@/pages/tools/RentVsBuyCalculatorPage'));
const HRAPage               = React.lazy(() => import('@/pages/tools/HRACalculatorPage'));
const GratuityPage          = React.lazy(() => import('@/pages/tools/GratuityCalculatorPage'));
const CarLoanPage           = React.lazy(() => import('@/pages/tools/CarLoanCalculatorPage'));
const RentReceiptPage       = React.lazy(() => import('@/pages/tools/RentReceiptPage'));
const CreditCardTrapPage    = React.lazy(() => import('@/pages/tools/CreditCardTrapPage'));
const HomeLoanPrepayPage    = React.lazy(() => import('@/pages/tools/HomeLoanPrepayPage'));
const RentalAgreementPage   = React.lazy(() => import('@/pages/tools/RentalAgreementPage'));
const StampDutyPage         = React.lazy(() => import('@/pages/tools/StampDutyCalculatorPage'));
const CapitalGainsPage      = React.lazy(() => import('@/pages/tools/CapitalGainsTaxPage'));
const StepUpSIPPage         = React.lazy(() => import('@/pages/tools/StepUpSIPCalculatorPage'));
const SSYPage               = React.lazy(() => import('@/pages/tools/SSYCalculatorPage'));

// TEMP: BuildTracker disabled — re-enable once tested
// const BuildTrackerPage       = React.lazy(() => import('@/pages/BuildTrackerPage'));
// const BuildTrackerDetailPage = React.lazy(() => import('@/pages/BuildTrackerDetailPage'));

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
export const API = `${BACKEND_URL}/api`;
export { BACKEND_URL };

// Global error boundary — catches chunk-load failures and render errors.
// Shows a friendly "refresh" prompt instead of a blank page.
class AppErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { error: null }; }
  static getDerivedStateFromError(e) { return { error: e }; }
  render() {
    if (!this.state.error) return this.props.children;
    const isChunkError = this.state.error?.name === 'ChunkLoadError'
      || this.state.error?.message?.includes('Loading chunk')
      || this.state.error?.message?.includes('dynamically imported module');
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#fafaf9', flexDirection: 'column', gap: 16, padding: 24 }}>
        <p style={{ fontSize: 32 }}>🔄</p>
        <p style={{ fontWeight: 700, fontSize: 18, color: '#1c1917', textAlign: 'center' }}>
          {isChunkError ? 'App updated — please refresh' : 'Something went wrong'}
        </p>
        <p style={{ fontSize: 14, color: '#78716c', textAlign: 'center', maxWidth: 320 }}>
          {isChunkError
            ? 'A newer version of Budget Mantra is available. Refresh to load it.'
            : 'An unexpected error occurred. Refresh the page to try again.'}
        </p>
        <button
          onClick={() => window.location.reload()}
          style={{ background: '#f97316', color: '#fff', border: 'none', borderRadius: 12, padding: '10px 24px', fontWeight: 700, fontSize: 15, cursor: 'pointer' }}
        >
          Refresh
        </button>
      </div>
    );
  }
}

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const tokenExists = !!localStorage.getItem('token');

  if (loading || (tokenExists && !user)) {
    return <SplashLoader />;
  }

  if (!user) return <Navigate to="/login" replace />;

  return <AppLayout>{children}</AppLayout>;
};

// Public Route Component (redirect to chatbot if logged in — Chanakya is the entry point)
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <SplashLoader />;
  }

  if (user) {
    return <Navigate to="/chatbot" replace />;
  }

  return children;
};

// ── Inactivity auto-logout ────────────────────────────────────────────────────
const IDLE_WARN_MS   = 25 * 60 * 1000;      // warn at 25 min
const IDLE_OUT_MS    = 30 * 60 * 1000;      // logout at 30 min (while tab is open)
const MAX_SESSION_MS = 24 * 60 * 60 * 1000; // hard logout after 24 h since last activity
const CHECK_MS       = 30 * 1000;           // check every 30s
const LS_ACTIVITY    = 'bm_last_activity';
const LS_LOGIN_TIME  = 'bm_login_time';

function InactivityGuard() {
  const { user, logout } = useAuth();
  const warnShown    = useRef(false);
  const warnToastId  = useRef(null);

  useEffect(() => {
    if (!user) return;

    // ── Check 1: max session age since login ──────────────────────────────
    const loginTime = parseInt(localStorage.getItem(LS_LOGIN_TIME) || '0', 10);
    if (loginTime && Date.now() - loginTime >= MAX_SESSION_MS) {
      logout();
      toast.error("Session expired — please sign in again.", { duration: 6000 });
      return;
    }
    // First-time guard for existing sessions that predate this feature
    if (!loginTime) {
      localStorage.setItem(LS_LOGIN_TIME, String(Date.now()));
    }

    // ── Check 2: idle time since last interaction ─────────────────────────
    const stored = parseInt(localStorage.getItem(LS_ACTIVITY) || '0', 10);
    if (stored && Date.now() - stored >= MAX_SESSION_MS) {
      logout();
      toast.error("Session expired — please sign in again.", { duration: 6000 });
      return;
    }

    // Initialise / refresh the stored timestamp
    localStorage.setItem(LS_ACTIVITY, String(Date.now()));

    const reset = () => {
      localStorage.setItem(LS_ACTIVITY, String(Date.now()));
      if (warnShown.current) {
        toast.dismiss(warnToastId.current);
        warnShown.current = false;
      }
    };

    const EVENTS = ["mousemove", "mousedown", "keydown", "scroll", "touchstart"];
    EVENTS.forEach(e => window.addEventListener(e, reset, { passive: true }));

    const timer = setInterval(() => {
      const last = parseInt(localStorage.getItem(LS_ACTIVITY) || String(Date.now()), 10);
      const idle = Date.now() - last;
      if (idle >= IDLE_OUT_MS) {
        clearInterval(timer);
        toast.dismiss(warnToastId.current);
        logout();
        localStorage.removeItem(LS_ACTIVITY);
        toast.error("You've been signed out due to inactivity.", { duration: 6000 });
      } else if (idle >= IDLE_WARN_MS && !warnShown.current) {
        warnShown.current = true;
        const secsLeft = Math.round((IDLE_OUT_MS - idle) / 1000);
        warnToastId.current = toast.warning(
          `No activity detected. You'll be signed out in ${secsLeft} seconds.`,
          {
            duration: IDLE_OUT_MS - idle,
            action: { label: "Stay logged in", onClick: reset },
          }
        );
      }
    }, CHECK_MS);

    return () => {
      EVENTS.forEach(e => window.removeEventListener(e, reset));
      clearInterval(timer);
    };
  }, [user, logout]);

  return null;
}

function CalcRoute({ publicEl, redirectTo }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) return <Navigate to={redirectTo} replace />;
  return publicEl;
}

// Same pattern for /tools/* — logged-in users go to the real tool
function ToolRoute({ children, redirectTo }) {
  const { user, loading } = useAuth();
  // Show content immediately — never block public tools with a blank screen
  if (loading) return children;
  // Only redirect logged-in users when a specific redirectTo is provided
  if (user && redirectTo) return <Navigate to={redirectTo} replace />;
  return children;
}

// TEMP: JourneyRoute disabled
// function JourneyRoute({ children }) {
//   const { user, loading } = useAuth();
//   const tokenExists = !!localStorage.getItem('token');
//   if (loading || (tokenExists && !user)) return <SplashLoader />;
//   if (user) return <AppLayout><Suspense fallback={<SplashLoader />}>{children}</Suspense></AppLayout>;
//   return <PublicShell wide><Suspense fallback={<SplashLoader />}>{children}</Suspense></PublicShell>;
// }

// App-level upgrade modal — rendered inside AuthProvider so useUpgrade works
// Scroll #app-main-scroll to top on every route change
function RouteScrollReset() {
  const { pathname } = useLocation();
  useEffect(() => {
    const el = document.getElementById('app-main-scroll');
    if (el) el.scrollTop = 0;
    else window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function AppShell() {
  const { upgradeModal, clearUpgradeModal } = useUpgrade();

  return (
    <div className="App">
      <Toaster position="top-right" />
      <BrowserRouter>
        <RouteScrollReset />
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
          <Route path="/signup" element={<PublicRoute><SignupPage /></PublicRoute>} />
          <Route path="/forgot-password" element={<PublicRoute><ForgotPasswordPage /></PublicRoute>} />
          {/* Reset-password must render for ANY visitor, even if they have a
              stale token. PublicRoute would either redirect a logged-in user
              to /chatbot or trigger a 401 logout → /login, losing the reset
              token from the URL. */}
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/shared/:token" element={<SharedDashboard />} />
          <Route path="/nominee-login" element={<NomineeLogin />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/onboarding" element={<ProtectedRoute><OnboardingPage /></ProtectedRoute>} />
          <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
          <Route path="/calc/emi"    element={<CalcRoute publicEl={<PublicShell><CalcEmi /></PublicShell>}    redirectTo="/emis" />} />
          <Route path="/calc/sip"    element={<CalcRoute publicEl={<PublicShell><CalcSip /></PublicShell>}    redirectTo="/investments" />} />
          <Route path="/calc/salary" element={<CalcRoute publicEl={<PublicShell><CalcSalary /></PublicShell>} redirectTo="/income" />} />
          <Route path="/calc/tax"    element={<CalcRoute publicEl={<PublicShell><CalcTax /></PublicShell>}    redirectTo="/fire" />} />

          {/* Learn Hub — always public */}
          {/* <Route path="/journeys"        element={<JourneyRoute><JourneysHub /></JourneyRoute>} /> */}
          {/* <Route path="/journeys/:slug" element={<JourneyRoute><StoryJourneyPage /></JourneyRoute>} /> */}
          <Route path="/learn"            element={<Suspense fallback={<SplashLoader />}><PublicShell wide><LearnHub /></PublicShell></Suspense>} />
          <Route path="/learn/how-ai-works" element={<Suspense fallback={<SplashLoader />}><PublicShell wide><HowAIWorksPage /></PublicShell></Suspense>} />
          <Route path="/learn/:slug"      element={<Suspense fallback={<SplashLoader />}><PublicShell wide><ArticlePage /></PublicShell></Suspense>} />

          {/* SEO Tools Hub — public only; logged-in users redirect to real tools */}
          <Route path="/tools"                   element={<ToolRoute redirectTo="/insights"><Suspense fallback={<SplashLoader />}><PublicShell wide><ToolsHub /></PublicShell></Suspense></ToolRoute>} />
          <Route path="/tools/emi-calculator"    element={<ToolRoute redirectTo="/emis"><Suspense fallback={<SplashLoader />}><PublicShell wide><EMICalculatorPage /></PublicShell></Suspense></ToolRoute>} />
          <Route path="/tools/sip-calculator"    element={<ToolRoute redirectTo="/investments"><Suspense fallback={<SplashLoader />}><PublicShell wide><SIPCalculatorPage /></PublicShell></Suspense></ToolRoute>} />
          <Route path="/tools/salary-calculator" element={<ToolRoute redirectTo="/income"><Suspense fallback={<SplashLoader />}><PublicShell wide><SalaryCalculatorPage /></PublicShell></Suspense></ToolRoute>} />
          <Route path="/tools/fd-calculator"     element={<ToolRoute redirectTo="/investments"><Suspense fallback={<SplashLoader />}><PublicShell wide><FDCalculatorPage /></PublicShell></Suspense></ToolRoute>} />
          <Route path="/tools/ppf-calculator"    element={<ToolRoute redirectTo="/investments"><Suspense fallback={<SplashLoader />}><PublicShell wide><PPFCalculatorPage /></PublicShell></Suspense></ToolRoute>} />
          <Route path="/tools/fire-calculator"   element={<ToolRoute redirectTo="/fire"><Suspense fallback={<SplashLoader />}><PublicShell wide><FIRECalculatorPage /></PublicShell></Suspense></ToolRoute>} />
          <Route path="/tools/rent-vs-buy"       element={<ToolRoute redirectTo="/when-to-buy"><Suspense fallback={<SplashLoader />}><PublicShell wide><RentVsBuyPage /></PublicShell></Suspense></ToolRoute>} />
          <Route path="/tools/education-loan-calculator" element={<ToolRoute redirectTo="/emis"><Suspense fallback={<SplashLoader />}><PublicShell wide><EducationLoanPage /></PublicShell></Suspense></ToolRoute>} />
          <Route path="/tools/lumpsum-calculator"        element={<ToolRoute redirectTo="/investments"><Suspense fallback={<SplashLoader />}><PublicShell wide><LumpsumPage /></PublicShell></Suspense></ToolRoute>} />
          <Route path="/tools/epf-calculator"            element={<ToolRoute redirectTo="/investments"><Suspense fallback={<SplashLoader />}><PublicShell wide><EPFPage /></PublicShell></Suspense></ToolRoute>} />
          <Route path="/tools/xirr-calculator"           element={<ToolRoute redirectTo="/investments"><Suspense fallback={<SplashLoader />}><PublicShell wide><XIRRPage /></PublicShell></Suspense></ToolRoute>} />
          <Route path="/tools/income-tax-calculator"     element={<ToolRoute redirectTo="/income"><Suspense fallback={<SplashLoader />}><PublicShell wide><IncomeTaxPage /></PublicShell></Suspense></ToolRoute>} />
          <Route path="/tools/home-affordability-calculator" element={<ToolRoute><Suspense fallback={<SplashLoader />}><PublicShell wide><HomeAffordabilityPage /></PublicShell></Suspense></ToolRoute>} />
          <Route path="/tools/rent-vs-buy-calculator"        element={<ToolRoute><Suspense fallback={<SplashLoader />}><PublicShell wide><RentVsBuyCalcPage /></PublicShell></Suspense></ToolRoute>} />
          <Route path="/tools/hra-calculator"                element={<ToolRoute><Suspense fallback={<SplashLoader />}><PublicShell wide><HRAPage /></PublicShell></Suspense></ToolRoute>} />
          <Route path="/tools/gratuity-calculator"           element={<ToolRoute><Suspense fallback={<SplashLoader />}><PublicShell wide><GratuityPage /></PublicShell></Suspense></ToolRoute>} />
          <Route path="/tools/car-loan-calculator"           element={<ToolRoute redirectTo="/emis"><Suspense fallback={<SplashLoader />}><PublicShell wide><CarLoanPage /></PublicShell></Suspense></ToolRoute>} />
          <Route path="/tools/rent-receipt-generator"       element={<ToolRoute><Suspense fallback={<SplashLoader />}><PublicShell wide><RentReceiptPage /></PublicShell></Suspense></ToolRoute>} />
          <Route path="/tools/credit-card-trap"             element={<ToolRoute redirectTo="/emis"><Suspense fallback={<SplashLoader />}><PublicShell wide><CreditCardTrapPage /></PublicShell></Suspense></ToolRoute>} />
          <Route path="/tools/home-loan-prepay-calculator"  element={<ToolRoute redirectTo="/emis"><Suspense fallback={<SplashLoader />}><PublicShell wide><HomeLoanPrepayPage /></PublicShell></Suspense></ToolRoute>} />
          <Route path="/tools/rental-agreement"            element={<ToolRoute><Suspense fallback={<SplashLoader />}><PublicShell wide><RentalAgreementPage /></PublicShell></Suspense></ToolRoute>} />
          <Route path="/tools/stamp-duty-calculator"  element={<ToolRoute><Suspense fallback={<SplashLoader />}><PublicShell wide><StampDutyPage /></PublicShell></Suspense></ToolRoute>} />
          <Route path="/tools/capital-gains-tax"      element={<ToolRoute><Suspense fallback={<SplashLoader />}><PublicShell wide><CapitalGainsPage /></PublicShell></Suspense></ToolRoute>} />
          <Route path="/tools/step-up-sip-calculator" element={<ToolRoute><Suspense fallback={<SplashLoader />}><PublicShell wide><StepUpSIPPage /></PublicShell></Suspense></ToolRoute>} />
          <Route path="/tools/sukanya-samriddhi"      element={<ToolRoute><Suspense fallback={<SplashLoader />}><PublicShell wide><SSYPage /></PublicShell></Suspense></ToolRoute>} />

          {/* Protected Routes */}
          <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
          <Route path="/budget" element={<ProtectedRoute><BudgetManager /></ProtectedRoute>} />
          <Route path="/emis" element={<ProtectedRoute><EMIManager /></ProtectedRoute>} />
          <Route path="/transactions" element={<Navigate to="/budget" replace />} />
          <Route path="/sms-tracker" element={<Navigate to="/data" replace />} />
          <Route path="/when-to-buy" element={<ProtectedRoute><WhenToBuy /></ProtectedRoute>} />
          <Route path="/smart-buy"   element={<ProtectedRoute><SmartBuy /></ProtectedRoute>} />
          <Route path="/notes"       element={<ProtectedRoute><QuickNotesPage /></ProtectedRoute>} />
          <Route path="/family" element={<ProtectedRoute><FamilyManagement /></ProtectedRoute>} />
          <Route path="/chatbot" element={<ProtectedRoute><Chatbot /></ProtectedRoute>} />
          <Route path="/savings-goals" element={<ProtectedRoute><SavingsGoals /></ProtectedRoute>} />
          <Route path="/investments" element={<ProtectedRoute><InvestmentTracker /></ProtectedRoute>} />
          <Route path="/gold" element={<Navigate to="/investments" replace />} />
          <Route path="/hand-loans" element={<ProtectedRoute><HandLoanTracker /></ProtectedRoute>} />
          {/* <Route path="/chit-funds" element={<ProtectedRoute><ChitFundTracker /></ProtectedRoute>} /> */}
          {/* <Route path="/tools/chit-fund-calculator" element={<ToolRoute><Suspense fallback={<SplashLoader />}><ChitFundPublicPage /></Suspense></ToolRoute>} /> */}
          <Route path="/credit-cards" element={<ProtectedRoute><CreditCardTracker /></ProtectedRoute>} />
          <Route path="/trips" element={<ProtectedRoute><TripPlanner /></ProtectedRoute>} />
          <Route path="/trips/shared/:token" element={<SharedTrip />} />
          {/* <Route path="/build-tracker" element={<Suspense fallback={<SplashLoader />}><AppLayout><BuildTrackerPage /></AppLayout></Suspense>} /> */}
          {/* <Route path="/build-tracker/:id" element={<Suspense fallback={<SplashLoader />}><AppLayout><BuildTrackerDetailPage /></AppLayout></Suspense>} /> */}
          <Route path="/group-expenses" element={<ProtectedRoute><GroupExpenses /></ProtectedRoute>} />
          <Route path="/calendar" element={<ProtectedRoute><FinancialCalendar /></ProtectedRoute>} />
          <Route path="/timeline" element={<Navigate to="/dashboard" replace />} />
          <Route path="/luxury" element={<Navigate to="/investments" replace />} />
          <Route path="/children" element={<Navigate to="/dashboard" replace />} />
          <Route path="/fire" element={<ProtectedRoute><FireCalculator /></ProtectedRoute>} />
          <Route path="/investment-advisor" element={<ProtectedRoute><InvestmentAdvisor /></ProtectedRoute>} />
          <Route path="/subscriptions" element={<Navigate to="/budget?tab=recurring" replace />} />
          <Route path="/recurring" element={<Navigate to="/budget" replace />} />
          <Route path="/income" element={<ProtectedRoute><IncomePage /></ProtectedRoute>} />
          <Route path="/upi-parser" element={<Navigate to="/data" replace />} />
          <Route path="/events" element={<Navigate to="/trips" replace />} />
          <Route path="/data" element={<ProtectedRoute><DataManagement /></ProtectedRoute>} />
          <Route path="/historical-entry" element={<ProtectedRoute><HistoricalEntry /></ProtectedRoute>} />
          <Route path="/bm-admin/login" element={<Suspense fallback={<SplashLoader />}><AdminLogin /></Suspense>} />
          <Route path="/bm-admin"       element={<Suspense fallback={<SplashLoader />}><AdminDashboard /></Suspense>} />
          <Route path="/circle" element={<ProtectedRoute><CirclePage /></ProtectedRoute>} />
          <Route path="/gifts" element={<ProtectedRoute><GiftTracker /></ProtectedRoute>} />
          <Route path="/recurring" element={<ProtectedRoute><RecurringExpenses /></ProtectedRoute>} />
          <Route path="/lifetime-earnings" element={<ProtectedRoute><LifetimeEarnings /></ProtectedRoute>} />
          <Route path="/life-profile" element={<ProtectedRoute><LifeProfile /></ProtectedRoute>} />
          <Route path="/playbook" element={<ProtectedRoute><Playbook /></ProtectedRoute>} />
          <Route path="/insights" element={<ProtectedRoute><InsightsPage /></ProtectedRoute>} />
          <Route path="/support" element={<ProtectedRoute><SupportPage /></ProtectedRoute>} />
          <Route path="/more" element={<ProtectedRoute><MorePage /></ProtectedRoute>} />
          {/* Catch-all — unknown URLs go to tools hub (better than blank) */}
          <Route path="*" element={<Navigate to="/tools" replace />} />
        </Routes>

        {/* Floating Chanakya chat widget — inside Router so useLocation works */}
        <ChanakyaWidget />
      </BrowserRouter>

      {/* Global upgrade modal — catches any 402 from any page */}
      <UpgradeModal
        open={upgradeModal.open}
        onClose={clearUpgradeModal}
        resource={upgradeModal.resource}
        limit={upgradeModal.limit}
      />

      {/* Inactivity auto-logout guard */}
      <InactivityGuard />

      {/* Security watermark + screenshot deterrence */}
      <SecurityOverlay />
    </div>
  );
}

function App() {
  return (
    <AppErrorBoundary>
      <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID || '1088916328823-9mef6vcg47bj1gfgml9ljp62mf4r9r60.apps.googleusercontent.com'}>
        <ThemeProvider>
          <PrivacyProvider>
          <AuthProvider>
            <ConfirmProvider>
            <DashboardProvider>
              <AppShell />
            </DashboardProvider>
            </ConfirmProvider>
          </AuthProvider>
          </PrivacyProvider>
        </ThemeProvider>
      </GoogleOAuthProvider>
    </AppErrorBoundary>
  );
}

export default App;
